import { getApiUrls } from "./apiUrl";
import { exchangeAssessmentPreauth } from "../services/assessmentPreauthService";

const TOKEN_STORAGE_KEY = "assessment_client_auth_token";
const IMITATION_USER_STORAGE_KEY = "assessment_client_imitation_user_id";
const BENCHMARK_ID_STORAGE_KEY = "assessment_client_benchmark_id";
const ACTIVITY_ID_STORAGE_KEY = "assessment_client_activity_id";
const RETURN_TO_STORAGE_KEY = "assessment_client_return_to";
const CODE_QUERY_PARAM_KEY = "code";
const TOKEN_QUERY_PARAM_KEY = "token";
const IMITATION_QUERY_PARAM_KEY = "imitationUserId";
const BENCHMARK_ID_QUERY_PARAM_KEY = "benchmarkId";
const ACTIVITY_ID_QUERY_PARAM_KEY = "activityId";
const RETURN_TO_QUERY_PARAM_KEY = "returnTo";
const REDIRECT_TO_QUERY_PARAM_KEY = "redirectTo";
export type AssessmentLaunchTaskType = "activity" | "benchmark";

const preauthExchangePromisesByCode = new Map<string, Promise<string | null>>();

const resolvePathWithRedirectTo = (
  pathname: string,
  redirectTo: string,
): string => {
  const normalizedBasePath = pathname.endsWith("/")
    ? pathname.slice(0, -1)
    : pathname;
  const normalizedRedirectPath = redirectTo.startsWith("/")
    ? redirectTo
    : `/${redirectTo}`;
  return `${normalizedBasePath}${normalizedRedirectPath}`;
};

export function getPreauthCodeFromUrl(
  url: URL = new URL(window.location.href),
): string | null {
  const code = url.searchParams.get(CODE_QUERY_PARAM_KEY)?.trim();
  return code || null;
}

const stripKnownParamsFromUrl = (url: URL): void => {
  const redirectTo = url.searchParams.get(REDIRECT_TO_QUERY_PARAM_KEY);
  url.searchParams.delete(CODE_QUERY_PARAM_KEY);
  url.searchParams.delete(TOKEN_QUERY_PARAM_KEY);
  url.searchParams.delete(IMITATION_QUERY_PARAM_KEY);
  url.searchParams.delete(BENCHMARK_ID_QUERY_PARAM_KEY);
  url.searchParams.delete(ACTIVITY_ID_QUERY_PARAM_KEY);
  url.searchParams.delete(RETURN_TO_QUERY_PARAM_KEY);
  url.searchParams.delete(REDIRECT_TO_QUERY_PARAM_KEY);

  const pathname = redirectTo
    ? resolvePathWithRedirectTo(url.pathname, redirectTo)
    : url.pathname;
  window.history.replaceState({}, "", `${pathname}${url.search}${url.hash}`);
};

function applyLaunchContextFromUrl(url: URL, hasNewSession: boolean): void {
  const benchmarkId = url.searchParams.get(BENCHMARK_ID_QUERY_PARAM_KEY);
  if (benchmarkId?.trim()) {
    sessionStorage.setItem(BENCHMARK_ID_STORAGE_KEY, benchmarkId.trim());
  } else if (hasNewSession) {
    sessionStorage.removeItem(BENCHMARK_ID_STORAGE_KEY);
  }

  const activityId = url.searchParams.get(ACTIVITY_ID_QUERY_PARAM_KEY);
  if (activityId?.trim()) {
    sessionStorage.setItem(ACTIVITY_ID_STORAGE_KEY, activityId.trim());
  } else if (hasNewSession) {
    sessionStorage.removeItem(ACTIVITY_ID_STORAGE_KEY);
  }

  const returnTo = url.searchParams.get(RETURN_TO_QUERY_PARAM_KEY);
  if (returnTo?.trim()) {
    sessionStorage.setItem(RETURN_TO_STORAGE_KEY, returnTo.trim());
  } else if (hasNewSession) {
    sessionStorage.removeItem(RETURN_TO_STORAGE_KEY);
  }
}

function applyAssessmentSessionFromExchange(result: {
  token: string;
  impersonated_user_id?: string;
}): void {
  sessionStorage.setItem(TOKEN_STORAGE_KEY, result.token);
  const impersonatedUserId = result.impersonated_user_id?.trim();
  if (impersonatedUserId) {
    sessionStorage.setItem(IMITATION_USER_STORAGE_KEY, impersonatedUserId);
  } else {
    sessionStorage.removeItem(IMITATION_USER_STORAGE_KEY);
  }
}

const parseSessionFromRedirect = (): void => {
  const url = new URL(window.location.href);
  if (getPreauthCodeFromUrl(url)) {
    return;
  }

  let hasSessionParams = false;

  const token = url.searchParams.get(TOKEN_QUERY_PARAM_KEY);
  if (token) {
    sessionStorage.setItem(TOKEN_STORAGE_KEY, token);
    hasSessionParams = true;
  }

  const imitationUserId = url.searchParams.get(IMITATION_QUERY_PARAM_KEY);
  if (imitationUserId) {
    sessionStorage.setItem(IMITATION_USER_STORAGE_KEY, imitationUserId);
    hasSessionParams = true;
  }

  const hasNewSessionRedirect = Boolean(token || imitationUserId);
  applyLaunchContextFromUrl(url, hasNewSessionRedirect);

  if (url.searchParams.get(BENCHMARK_ID_QUERY_PARAM_KEY)?.trim()) {
    hasSessionParams = true;
  }
  if (url.searchParams.get(ACTIVITY_ID_QUERY_PARAM_KEY)?.trim()) {
    hasSessionParams = true;
  }
  if (url.searchParams.get(RETURN_TO_QUERY_PARAM_KEY)?.trim()) {
    hasSessionParams = true;
  }

  if (hasSessionParams) {
    stripKnownParamsFromUrl(url);
  }
};

const clearAuthCredentials = (): void => {
  sessionStorage.removeItem(TOKEN_STORAGE_KEY);
  sessionStorage.removeItem(IMITATION_USER_STORAGE_KEY);
};

async function exchangePreauthCodeFromUrl(
  url: URL,
  code: string,
): Promise<string | null> {
  applyLaunchContextFromUrl(url, true);
  stripKnownParamsFromUrl(url);

  clearAuthCredentials();

  const { assessmentsApiUrl } = getApiUrls();
  if (!assessmentsApiUrl) {
    return "Assessments API URL is not configured.";
  }

  try {
    const result = await exchangeAssessmentPreauth({
      apiUrl: assessmentsApiUrl,
      code,
    });
    applyAssessmentSessionFromExchange(result);
    return null;
  } catch (exchangeError: unknown) {
    return exchangeError instanceof Error
      ? exchangeError.message
      : "Unable to exchange preauth code.";
  }
}

export async function bootstrapAssessmentSessionFromUrl(
  locationHref: string = window.location.href,
): Promise<string | null> {
  const url = new URL(locationHref);
  const code = getPreauthCodeFromUrl(url);
  if (!code) {
    return null;
  }

  const inFlightExchange = preauthExchangePromisesByCode.get(code);
  if (inFlightExchange) {
    return inFlightExchange;
  }

  const exchangePromise = exchangePreauthCodeFromUrl(url, code).finally(() => {
    preauthExchangePromisesByCode.delete(code);
  });
  preauthExchangePromisesByCode.set(code, exchangePromise);
  return exchangePromise;
}

export const getAuthToken = (): string | null => {
  parseSessionFromRedirect();
  return sessionStorage.getItem(TOKEN_STORAGE_KEY);
};

export const getImitationUserId = (): string | null => {
  parseSessionFromRedirect();
  return sessionStorage.getItem(IMITATION_USER_STORAGE_KEY);
};

export const getBenchmarkId = (): string | null => {
  parseSessionFromRedirect();
  return sessionStorage.getItem(BENCHMARK_ID_STORAGE_KEY);
};

export const getActivityId = (): string | null => {
  parseSessionFromRedirect();
  return sessionStorage.getItem(ACTIVITY_ID_STORAGE_KEY);
};

export const getReturnTo = (): string | null => {
  parseSessionFromRedirect();
  return sessionStorage.getItem(RETURN_TO_STORAGE_KEY);
};

export const clearAuthToken = (): void => {
  clearAuthCredentials();
  sessionStorage.removeItem(BENCHMARK_ID_STORAGE_KEY);
  sessionStorage.removeItem(ACTIVITY_ID_STORAGE_KEY);
  sessionStorage.removeItem(RETURN_TO_STORAGE_KEY);
};

export const hasPendingPreauthCodeInUrl = (): boolean => {
  return getPreauthCodeFromUrl() !== null;
};

export function resolveLaunchTaskType(): AssessmentLaunchTaskType {
  if (getBenchmarkId() && getActivityId()) {
    return "activity";
  }
  return "benchmark";
}
